/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';

import { Radio } from '@base-ui/react/radio';
import { RadioGroup } from '@base-ui/react/radio-group';

import { CalendarDate, type DateValue } from '@internationalized/date';
import type { RangeValue } from 'react-aria';

import { DatePicker, localToday, Label, useBreakpoint } from 'sarsaparilla';

import { RangeCalendar } from './calendars/RangeCalendar';
import { useScrollTopOnClear } from './calendars/date-picker-utils';
import { NumberField } from './NumberField';
import { TypeInDatesAccordion } from './TypeInDatesAccordion';
import type { DatePickerState, DatePickerVariant } from './DatePicker.types';
import {
    CALENDAR_MONTHS_COUNT,
    clampEndToMaxDate,
    getHasSetDates,
    getMaxTypedNights,
    MAX_SELECTABLE_NIGHTS,
    maxSelectableDate,
    PlusMinusIconSmall,
} from './DatePicker.utils';

import styles from './DatesPanel.module.scss';

// Module-level so the memoized RangeCalendar gets the same function every render.
function isOutsideSelectableRange(date: DateValue) {
    return date.compare(localToday) < 0 || date.compare(maxSelectableDate) > 0;
}

/**
 * While an end date is being picked, also rules out dates more than
 * MAX_SELECTABLE_NIGHTS from the first click, in either direction. The calendar
 * then stops the range at that limit, matching the type-in nights field.
 */
function isDateUnavailable(date: DateValue, anchorDate: CalendarDate | null) {
    if (isOutsideSelectableRange(date)) {
        return true;
    }

    return anchorDate
        ? Math.abs(date.compare(anchorDate)) > MAX_SELECTABLE_NIGHTS
        : false;
}

interface DatesPanelProps {
    isFlexibleAllowed?: boolean;
    state: DatePickerState;
    setState: React.Dispatch<React.SetStateAction<DatePickerState>>;
    variant: DatePickerVariant;
    isInTab?: boolean;
}

export function DatesPanel({
    isFlexibleAllowed,
    state,
    setState,
    variant,
    isInTab,
}: DatesPanelProps) {
    // Always a date: the calendar treats a null focusedValue as uncontrolled, so
    // the first month navigation would flip it to controlled.
    const [focusedValue, setFocusedValue] = React.useState<CalendarDate>(
        () => state.startDate ?? localToday
    );
    const { hasSpecificDates } = getHasSetDates(state);
    const { isSmAndUp, isSmAndDown } = useBreakpoint();
    const scrollAreaRef = React.useRef<HTMLDivElement>(null);

    // Reset returns the stacked calendar to the top. Focus moves back to today
    // too, or tabbing into the calendar would scroll to the old date again.
    useScrollTopOnClear(scrollAreaRef, state.startDate, () => {
        setFocusedValue(localToday);
    });

    // Kept referentially stable so the memoized RangeCalendar, and its hundreds
    // of cells in the stacked layout, only re-render when the dates change.
    const calendarValue = React.useMemo(
        () =>
            hasSpecificDates ? { start: state.startDate!, end: state.endDate! } : null,
        [hasSpecificDates, state.startDate, state.endDate]
    );

    const onCalendarChange = React.useCallback(
        (value: RangeValue<CalendarDate> | null) => {
            if (value?.start && value?.end) {
                const startDate =
                    value.start.compare(maxSelectableDate) > 0
                        ? maxSelectableDate
                        : value.start;
                const endDate = clampEndToMaxDate(startDate, value.end);

                setState((prev) => {
                    return {
                        ...prev,
                        startDate,
                        endDate,
                    };
                });
            }
        },
        [setState]
    );

    return (
        <div
            className={cx(styles.DatesPanel, {
                [styles.isDrawer]: variant === 'drawer',
            })}
            tabIndex={-1}
        >
            <div ref={scrollAreaRef} className={styles.ScrollArea}>
                <TypeInDatesAccordion title="Type in dates" isInTab={isInTab}>
                    <TypeInDates
                        state={state}
                        setState={setState}
                        setFocusedValue={setFocusedValue}
                    />
                </TypeInDatesAccordion>

                <div className={styles.RangeCalendarContainer}>
                    <RangeCalendar
                        numVisibleMonths={isSmAndUp ? 2 : CALENDAR_MONTHS_COUNT}
                        layout={isSmAndUp ? 'paged' : 'stacked'}
                        initialScrollDate={state.startDate}
                        size={isSmAndDown ? 'fluid' : 'default'}
                        value={calendarValue}
                        onChange={onCalendarChange}
                        isDateUnavailable={isDateUnavailable}
                        focusedValue={focusedValue}
                        onFocusChange={setFocusedValue}
                        maxValue={maxSelectableDate}
                        minValue={localToday}
                    />
                </div>
            </div>

            {isFlexibleAllowed ? (
                <div className={styles.PlusMinusRadioGroupContainer}>
                    <PlusMinusRadioGroup state={state} setState={setState} />
                </div>
            ) : null}
        </div>
    );
}

interface TypeInDatesProps {
    state: DatePickerState;
    setState: React.Dispatch<React.SetStateAction<DatePickerState>>;
    setFocusedValue: React.Dispatch<React.SetStateAction<CalendarDate>>;
}

/** A typed range whose nights were reduced to fit the booking window. */
interface ClampNotice {
    startDate: CalendarDate;
    endDate: CalendarDate;
}

/**
 * Keyboard entry for a start date and a number of nights.
 * Nights entered before a start date are held locally as pending. They never
 * reach the draft, so applying or closing with only nights selects nothing.
 */
function TypeInDates({ state, setState, setFocusedValue }: TypeInDatesProps) {
    const [typedStartDate, setTypedStartDate] = React.useState<CalendarDate | null>(
        state.startDate
    );
    const [pendingNights, setPendingNights] = React.useState(0);
    const [clamp, setClamp] = React.useState<ClampNotice | null>(null);

    const rangeNights =
        state.startDate && state.endDate ? state.endDate.compare(state.startDate) : null;
    const hasRange = rangeNights !== null;

    // The notice only describes the range it reduced. A calendar pick or Clear
    // Dates replaces that range, so the notice drops out of the live region and
    // the same reduction later is announced again.
    const clampMessage =
        clamp &&
        state.startDate?.compare(clamp.startDate) === 0 &&
        state.endDate?.compare(clamp.endDate) === 0
            ? `Number of nights reduced to ${clamp.endDate.compare(clamp.startDate)} to stay within the booking window.`
            : '';

    React.useEffect(() => {
        setTypedStartDate(state.startDate);
    }, [state.startDate]);

    // Any range, typed or picked on the calendar, replaces the pending count,
    // so clearing it later (e.g. Clear Dates) doesn't bring back a stale value.
    React.useEffect(() => {
        if (hasRange) {
            setPendingNights(0);
        }
    }, [hasRange]);

    const numberOfNights = rangeNights ?? pendingNights;
    const maxTypedNights = getMaxTypedNights(state.startDate);
    const nightsInputId = React.useId();

    return (
        <div className={styles.TypeInDates}>
            <div className={styles.noCalendarRow}>
                <DatePicker
                    label="Start Date"
                    hasCalendar={false}
                    maxValue={maxSelectableDate}
                    minValue={localToday}
                    hasFloatingError={true}
                    value={typedStartDate}
                    onChange={(value, validation) => {
                        setTypedStartDate(value);
                        setClamp(null);

                        const hasCompleteYear = value ? value.year >= 2000 : false;

                        if (value && !validation?.isInvalid && hasCompleteYear) {
                            const requestedNights = Math.max(0, numberOfNights);
                            const endDate = clampEndToMaxDate(
                                value,
                                value.add({ days: requestedNights })
                            );
                            const nights = endDate.compare(value);

                            if (nights < requestedNights) {
                                setClamp({ startDate: value, endDate });
                            }

                            setState((prev) => {
                                return {
                                    ...prev,
                                    startDate: value,
                                    endDate,
                                };
                            });
                            setFocusedValue(value);
                        }

                        if (!value) {
                            // Keep the stay length so retyping a start date restores it.
                            setPendingNights(numberOfNights);

                            setState((prev) => {
                                return {
                                    ...prev,
                                    startDate: null,
                                    endDate: null,
                                    flexStartRange: null,
                                    flexEndRange: null,
                                };
                            });
                        }
                    }}
                />
            </div>
            <div className={styles.noCalendarRow}>
                <Label htmlFor={nightsInputId} className="mb-half">
                    Number of Nights
                </Label>
                <NumberField
                    id={nightsInputId}
                    aria-label="Number of Nights"
                    min={0}
                    max={maxTypedNights}
                    step={1}
                    smallStep={1}
                    largeStep={5}
                    isFullWidth={true}
                    value={numberOfNights}
                    onValueChange={(value) => {
                        const requestedNights = value ?? 0;
                        setClamp(null);

                        if (!state.startDate) {
                            setPendingNights(
                                Math.min(
                                    Math.max(0, requestedNights),
                                    MAX_SELECTABLE_NIGHTS
                                )
                            );
                            return;
                        }

                        setState((prev) => {
                            if (!prev.startDate) {
                                return prev;
                            }

                            const nights = Math.min(
                                Math.max(0, requestedNights),
                                getMaxTypedNights(prev.startDate)
                            );

                            return {
                                ...prev,
                                endDate: prev.startDate.add({ days: nights }),
                            };
                        });
                    }}
                    className={styles.Field}
                />
            </div>
            <div className="rec-sr-only" role="status" aria-live="polite">
                {clampMessage}
            </div>
        </div>
    );
}

interface PlusMinusRadioGroupProps {
    state: DatePickerState;
    setState: React.Dispatch<React.SetStateAction<DatePickerState>>;
}

function PlusMinusRadioGroup({ state, setState }: PlusMinusRadioGroupProps) {
    return (
        <RadioGroup
            aria-label="Flexible dates"
            className={styles.PlusMinusRadioGroup}
            value={state.flexDays ?? 0}
            onValueChange={(value) => {
                setState((prev) => {
                    return {
                        ...prev,
                        flexDays: Number(value),
                    };
                });
            }}
        >
            <Radio.Root
                value={0}
                aria-label="Exact dates"
                className={styles.PlusMinusRadioButton}
            >
                <PlusMinusRadioLabel value="0" />
            </Radio.Root>

            <Radio.Root
                value={1}
                aria-label="Plus or minus 1 day"
                className={styles.PlusMinusRadioButton}
            >
                <PlusMinusRadioLabel value="1" />
            </Radio.Root>

            <Radio.Root
                value={2}
                aria-label="Plus or minus 2 days"
                className={styles.PlusMinusRadioButton}
            >
                <PlusMinusRadioLabel value="2" />
            </Radio.Root>

            <Radio.Root
                value={3}
                aria-label="Plus or minus 3 days"
                className={styles.PlusMinusRadioButton}
            >
                <PlusMinusRadioLabel value="3" />
            </Radio.Root>

            <Radio.Root
                value={7}
                aria-label="Plus or minus 7 days"
                className={styles.PlusMinusRadioButton}
            >
                <PlusMinusRadioLabel value="7" />
            </Radio.Root>
        </RadioGroup>
    );
}

function PlusMinusRadioLabel({ value }: { value: string }) {
    return (
        <>
            {value === '0' ? (
                'Exact dates'
            ) : (
                <>
                    <PlusMinusIconSmall />
                    {`${value} ${value === '1' ? 'day' : 'days'}`}
                </>
            )}
        </>
    );
}
